import type { MonoBitmap } from "./bitmap";
import { drawCode128Bars } from "./code128";
import {
  layoutItemLabel,
  layoutLabel,
  type ItemLabelInput,
  type LabelInput,
  type LaidOutLabel,
} from "./labelLayout";
import { toMonoBitmap } from "./receiptRender";

/**
 * G-84 — the label into the dots that burn. ONE path for the preview and the
 * print, as the receipt and the note do. Text goes through the canvas and the
 * 1-bit threshold; the BARS are painted onto the thresholded bitmap afterwards
 * at integer dot positions, so no antialiasing can ever widen or narrow one.
 */
export interface RenderedLabel {
  bitmap: MonoBitmap;
  layout: LaidOutLabel;
  lengthMm: number;
}

const DOTS_PER_MM = 8;
const FONT_STACK = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export function renderLabel(input: LabelInput, widthDots: number): RenderedLabel {
  return renderLaidOut(
    layoutLabel(input, widthDots, measurer()),
  );
}

/**
 * G-84 follow-up: the ITEM barcode sticker. Same one path to the dots — only
 * the arrangement differs, so a fix to the burning applies to both labels.
 */
export function renderItemLabel(input: ItemLabelInput, widthDots: number): RenderedLabel {
  return renderLaidOut(
    layoutItemLabel(input, widthDots, measurer()),
  );
}

function measurer() {
  const probe = document.createElement("canvas").getContext("2d")!;
  return (text: string, px: number, weight: "normal" | "bold") => {
    probe.font = `${weight} ${px}px ${FONT_STACK}`;
    return probe.measureText(text).width;
  };
}

function renderLaidOut(layout: LaidOutLabel): RenderedLabel {
  const canvas = document.createElement("canvas");
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  for (const op of layout.texts) {
    ctx.font = `${op.weight} ${op.px}px ${FONT_STACK}`;
    ctx.fillText(op.text, op.x, op.y);
  }

  const bitmap = toMonoBitmap(canvas);
  drawCode128Bars(bitmap, layout.barcode.encoded, {
    x: layout.barcode.x,
    y: layout.barcode.y,
    height: layout.barcode.height,
    moduleDots: layout.barcode.moduleDots,
  });
  return { bitmap, layout, lengthMm: Math.round(bitmap.height / DOTS_PER_MM) };
}
